import { useState, useEffect, useCallback } from 'react';
import { useSocket } from '../contexts/SocketContext';
import type { RoomView, RoomJoinedPayload, RoomUpdatedPayload, RoomErrorPayload } from '../types/room';

export interface UseRoomResult {
  room: RoomView | null;
  myId: string | null;
  isLeader: boolean;
  error: string | null;
  notices: { id: number; message: string }[];   // short messages for everyone (left, dropped, back, new host)
  log: string[];                                 // the same messages, newest first, kept for "In the room"
  removedBy: string | null;                      // set when the host removed this player
  takenOver: boolean;                            // this seat was opened on another device (a league game)
  leave: () => void;
  setName: (name: string) => void;
  setSpectator: (spectate: boolean) => void;
  setGame: (gameId: string, seats?: number) => void;
  arrange: (order: string[], watching: string[]) => void;
  setSeatConfig: (config: Record<string, unknown>) => void;
  suggest: (gameId: string | null, gameName?: string) => void;
  setConfig: (config: Record<string, unknown>) => void;
  setPlaytestMode: (enabled: boolean) => void;
  addBot: (botName?: string) => void;
  removeParticipant: (participantId: string) => void;
  transferLeader: (participantId: string) => void;
  start: () => void;
  finish: (result?: { winner?: string; how?: string }) => void;
  reportResult: (result: { winner?: string; how?: string } | null) => void;   // null: a new game started in place
}

export function useRoom(slug: string, name: string): UseRoomResult {
  const { socket, isConnected } = useSocket();
  const [room, setRoom] = useState<RoomView | null>(null);
  const [myId, setMyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notices, setNotices] = useState<{ id: number; message: string }[]>([]);
  const [log, setLog] = useState<string[]>([]);
  const [removedBy, setRemovedBy] = useState<string | null>(null);
  const [takenOver, setTakenOver] = useState(false);

  const tokenKey = `room-token-${slug}`;

  useEffect(() => {
    if (!socket || !isConnected || !name) return;
    const token = sessionStorage.getItem(tokenKey) ?? undefined;
    socket.emit('room:join', { slug, name, token });
  }, [socket, isConnected, slug, name, tokenKey]);

  useEffect(() => {
    if (!socket) return;

    const onJoined = ({ room, myId, myToken }: RoomJoinedPayload) => {
      setRoom(room);
      setMyId(myId);
      sessionStorage.setItem(tokenKey, myToken);
    };
    const onUpdated = ({ room }: RoomUpdatedPayload) => setRoom(room);
    const onError = ({ message }: RoomErrorPayload) => setError(message);
    const onNotice = ({ message }: { message: string }) => {
      const id = Date.now() + Math.random();
      setNotices(n => [...n.slice(-3), { id, message }]);
      setLog(l => [message, ...l].slice(0, 6));
      setTimeout(() => setNotices(n => n.filter(x => x.id !== id)), 7000);
    };
    const onRemoved = ({ by }: { by: string }) => { sessionStorage.removeItem(tokenKey); setRemovedBy(by); };
    // Opening the room again here takes the seat back (by name), so this tab's token is no longer needed.
    const onTakenOver = () => { sessionStorage.removeItem(tokenKey); setTakenOver(true); };

    socket.on('room:joined', onJoined);
    socket.on('room:updated', onUpdated);
    socket.on('room:error', onError);
    socket.on('room:notice', onNotice);
    socket.on('room:removed', onRemoved);
    socket.on('room:taken-over', onTakenOver);
    return () => {
      socket.off('room:taken-over', onTakenOver);
      socket.off('room:joined', onJoined);
      socket.off('room:updated', onUpdated);
      socket.off('room:error', onError);
      socket.off('room:notice', onNotice);
      socket.off('room:removed', onRemoved);
    };
  }, [socket, tokenKey]);

  const isLeader = room?.participants.find(p => p.id === myId)?.isLeader ?? false;

  const leave = useCallback(() => {
    socket?.emit('room:leave', { slug });
    sessionStorage.removeItem(tokenKey);
  }, [socket, slug, tokenKey]);

  const setName = useCallback((n: string) =>
    socket?.emit('room:set-name', { slug, name: n }), [socket, slug]);

  const setSpectator = useCallback((spectate: boolean) =>
    socket?.emit('room:set-spectator', { slug, spectate }), [socket, slug]);

  const setGame = useCallback((gameId: string, seats?: number) =>
    socket?.emit('room:set-game', { slug, gameId, seats }), [socket, slug]);

  const arrange = useCallback((order: string[], watching: string[]) =>
    socket?.emit('room:arrange', { slug, order, watching }), [socket, slug]);

  const setSeatConfig = useCallback((config: Record<string, unknown>) =>
    socket?.emit('room:set-seat-config', { slug, config }), [socket, slug]);

  const suggest = useCallback((gameId: string | null, gameName?: string) =>
    socket?.emit('room:suggest', { slug, gameId, gameName }), [socket, slug]);

  const setConfig = useCallback((config: Record<string, unknown>) =>
    socket?.emit('room:set-config', { slug, config }), [socket, slug]);

  const setPlaytestMode = useCallback((enabled: boolean) =>
    socket?.emit('room:set-playtestmode', { slug, enabled }), [socket, slug]);

  const addBot = useCallback((botName?: string) =>
    socket?.emit('room:add-bot', { slug, name: botName }), [socket, slug]);

  const removeParticipant = useCallback((participantId: string) =>
    socket?.emit('room:remove-participant', { slug, participantId }), [socket, slug]);

  const transferLeader = useCallback((participantId: string) =>
    socket?.emit('room:transfer-leader', { slug, participantId }), [socket, slug]);

  const start = useCallback(() =>
    socket?.emit('room:start', { slug }), [socket, slug]);

  const finish = useCallback((result?: { winner?: string; how?: string }) =>
    socket?.emit('room:finish', { slug, result }), [socket, slug]);

  const reportResult = useCallback((result: { winner?: string; how?: string } | null) =>
    socket?.emit('room:report-result', { slug, result }), [socket, slug]);

  return {
    room, myId, isLeader, error, notices, log, removedBy, takenOver,
    leave, setName, setSpectator, setGame, arrange, setSeatConfig, suggest, setConfig, setPlaytestMode,
    addBot, removeParticipant, transferLeader, start, finish, reportResult,
  };
}
